import React, { FC, useEffect, useState } from 'react'
import AppRouter from './router/AppRouter'
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react'
import { AuthError, InteractionRequiredAuthError, InteractionType } from '@azure/msal-browser'
import OrganizationsContextProvider from './context/OrganizationsContext'
import toast, { Toaster } from 'react-hot-toast'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { getCookie, portalUrl } from './services/http-common'
import { loginRequest, msalInstance } from './services/AuthConfig'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from './contexts/AuthContext'

const ErrorComponent: FC<{ error: AuthError | null }> = ({ error }) => {
  useEffect(() => {
    if (error instanceof InteractionRequiredAuthError) {
      // window.location.replace(`${portalUrl}?app=${window.location.href}`)
      msalInstance.acquireTokenRedirect(loginRequest)
    }
  }, [error])
  return null
}

export const AUTH_PROVIDER = process.env.REACT_APP_AUTH_PROVIDER

const App: FC<{ pca?: any }> = ({ pca }) => {
  const [first, setFirst] = useState(true)
  const handleError = (error: any): string => {
    let errorMessage = ''
    const response = error.response
    const data = response?.data
    const status = response?.status
    if (status === 404) {
      errorMessage = data?.message ?? 'Resource not found'
    } else if (status === 403) {
      errorMessage = 'Unauthorized request'
    } else if (error.code === 'ERR_NETWORK' || status === 401) {
      errorMessage = ''
    } else {
      errorMessage = data?.errorMessage ?? 'An error occurred!'
    }
    return errorMessage
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
      },
      mutations: {
        onError: (error) => {
          const errorMessage = handleError(error)
          if (!errorMessage) return
          toast.error(errorMessage)
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        const errorMessage = handleError(error)
        if (!errorMessage) return
        toast.error(errorMessage)
      },
    }),
  })

  useEffect(() => {
    if (AUTH_PROVIDER !== 'msal') return
    msalInstance
      .ssoSilent(loginRequest)
      .then((response) => {
        msalInstance.setActiveAccount(response.account)
        setFirst(false)
      })
      .catch((err) => {
        if (err instanceof InteractionRequiredAuthError) {
          window.location.replace(`${portalUrl}/logout`)
        }
      })
  }, [])

  useEffect(() => {
    if (AUTH_PROVIDER !== 'msal') return
    const interval = setInterval(() => {
      if (getCookie('refreshOrganization') === 'true') {
        document.cookie = `refreshOrganization=false; path=/; max-age=0; domain=.sistemiv.com`
        window.location.reload()
      }
    }, 1000)

    return () => clearInterval(interval)
  })

  return AUTH_PROVIDER === 'msal' ? (
    <MsalProvider instance={pca}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Silent}
        authenticationRequest={{
          scopes: ['openid', 'fd1442d1-cbcc-4b73-a893-a61b8d3d4782'],
        }}
        errorComponent={ErrorComponent}
      >
        {!first && (
          <AuthProvider>
            <OrganizationsContextProvider>
              <QueryClientProvider client={queryClient}>
                <AppRouter />
                <Toaster position='bottom-right' reverseOrder={false} />
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </OrganizationsContextProvider>
          </AuthProvider>
        )}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  ) : (
    <AuthProvider>
      <OrganizationsContextProvider>
        <QueryClientProvider client={queryClient}>
          <AppRouter />
          <Toaster position='bottom-right' reverseOrder={false} />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </OrganizationsContextProvider>
    </AuthProvider>
  )
}

export default App
